import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../../SubscriptionAppButton';
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  billingPeriod: 'month' | 'year' | 'bottle' | 'case';
  features: string[];
  imageUrl?: string;
  category?: string;
}
export interface ProductSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  selectedProductId?: string;
  replacementProductId?: string;
  onSelectProduct: (productId: string) => void;
  onSelectReplacement: (productId: string) => void;
  onConfirm: () => void;
}
export const ProductSwapModal = ({
  isOpen,
  onClose,
  products,
  selectedProductId,
  replacementProductId,
  onSelectProduct,
  onSelectReplacement,
  onConfirm
}: ProductSwapModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  if (!isOpen) return null;
  // Filter products based on search and exclude selected product in step 2
  const filteredProducts = products.filter(product => {
    // In step 2, exclude the product that was selected in step 1
    if (step === 2 && product.id === selectedProductId) {
      return false;
    }
    const query = searchQuery.toLowerCase();
    return product.name.toLowerCase().includes(query) || product.description.toLowerCase().includes(query) || product.category?.toLowerCase().includes(query) || product.features.some(f => f.toLowerCase().includes(query));
  });
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };
  const handleClose = () => {
    setStep(1);
    setSearchQuery('');
    onClose();
  };
  const handleNext = () => {
    if (step === 1 && selectedProductId) {
      setStep(2);
      setSearchQuery('');
    } else if (step === 2 && replacementProductId) {
      onConfirm();
      setStep(1);
      setSearchQuery('');
    }
  };
  const handleBack = () => {
    setStep(1);
    setSearchQuery('');
  };
  const handleProductSelect = (productId: string) => {
    if (step === 1) {
      onSelectProduct(productId);
    } else {
      onSelectReplacement(productId);
    }
  };
  const currentSelection = step === 1 ? selectedProductId : replacementProductId;
  const isNextDisabled = !currentSelection;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(15,23,42,0.5)] backdrop-blur-sm" onClick={handleBackdropClick} data-id="element-497">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" data-id="element-498">
        {/* Header */}
        <div className="p-6 pb-4" data-id="element-499">
          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-4" data-id="element-500">
            <span className="text-[14px] font-semibold text-[#0EA5E9]" data-id="element-501">
              Step {step} of 2
            </span>
            <div className="flex gap-1" data-id="element-502">
              <div className={`w-12 h-1 rounded-full ${step >= 1 ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-503" />
              <div className={`w-12 h-1 rounded-full ${step >= 2 ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-504" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-[24px] font-semibold text-[#0F172A] mb-2" data-id="element-505">
            {step === 1 ? 'Select Product to Swap' : 'Select Replacement Product'}
          </h2>
          <p className="text-[15px] text-[#64748B] mb-4" data-id="element-506">
            {step === 1 ? 'Choose which wine you want to replace' : 'Choose the new wine to swap in'}
          </p>

          {/* Search Bar */}
          <div className="relative" data-id="element-507">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#94A3B8]" size={18} data-id="element-508" />
            <input type="text" placeholder="Search for a product..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-[48px] pl-12 pr-4 border border-[#CBD5E1] rounded-lg text-[15px] text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all" data-id="element-509" />
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-y-auto px-6" data-id="element-510">
          {filteredProducts.length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-511">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mb-4" data-id="element-512">
                <Search size={24} className="text-[#94A3B8]" data-id="element-513" />
              </div>
              <p className="text-[16px] font-medium text-[#0F172A] mb-1" data-id="element-514">
                No wines found
              </p>
              <p className="text-[14px] text-[#64748B]" data-id="element-515">
                Try adjusting your search terms
              </p>
            </div> : <div className="space-y-2 pb-4" data-id="element-516">
              {filteredProducts.map(product => {
            const isSelected = product.id === currentSelection;
            return <button key={product.id} onClick={() => handleProductSelect(product.id)} className={`w-full p-4 rounded-lg text-left transition-all duration-200 flex items-center justify-between group ${isSelected ? 'bg-[#E0F2FE] border-2 border-[#0EA5E9]' : 'bg-white border-2 border-transparent hover:bg-[#F8FAFC]'}`} data-id="element-517">
                    {/* Left side - Wine icon and name */}
                    <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-518">
                      {/* Wine bottle icon */}
                      <div className="flex-shrink-0 w-6 h-6 text-[#94A3B8]" data-id="element-519">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-520">
                          <path d="M8 2h8" data-id="element-521" />
                          <path d="M9 2v6.5c0 1.5-1 3-1 4.5s1 3 1 4.5V22h6v-4.5c0-1.5 1-3 1-4.5s-1-3-1-4.5V2" data-id="element-522" />
                        </svg>
                      </div>

                      <span className="text-[16px] font-medium text-[#0F172A] truncate" data-id="element-523">
                        {product.name}
                      </span>
                    </div>

                    {/* Right side - Price and radio */}
                    <div className="flex items-center gap-3 flex-shrink-0" data-id="element-524">
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-525">
                        ${product.price.toFixed(0)}
                      </span>

                      {/* Radio button */}
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#0EA5E9] bg-white' : 'border-[#CBD5E1] bg-white'}`} data-id="element-526">
                        {isSelected && <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" data-id="element-527" />}
                      </div>
                    </div>
                  </button>;
          })}
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 pt-4 border-t border-[#E2E8F0]" data-id="element-528">
          <div data-id="element-529">
            {step === 2 && <Button variant="ghost" onClick={handleBack} data-id="element-530">
                Back
              </Button>}
          </div>
          <div className="flex items-center gap-3" data-id="element-531">
            <Button variant="outline" onClick={handleClose} data-id="element-532">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleNext} disabled={isNextDisabled} data-id="element-533">
              {step === 1 ? 'Next' : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>;
};